import { Attachment, Close } from "@mui/icons-material";
import {
  Alert,
  FormControlLabel,
  Icon,
  IconButton,
  InputAdornment,
  Radio,
  TextField,
} from "@mui/material";
import React from "react";
import { UrlOrFileInput } from "./UrlOrFileInput";
import { XMarkIcon } from "@heroicons/react/24/solid";

export const FILE_TYPE_UPLOADED = "file_type_uploaded";
export const FILE_TYPE_URL = "file_type_url";

type RadioWithFileReferenceProps = {
  label: string | React.ReactNode;
  value: string | number | null;
  url: string;
  filename: string;
  error?: string;
  onUrlChanged: (newUrl: string) => void;
  onFileDeleted: () => void;
  onFileChanged: React.ChangeEventHandler<HTMLInputElement>;
  onRadioChanged?: (e: React.SyntheticEvent<Element, Event>, checked: boolean) => void;
  hideUrlInput?: boolean;
  disabled?: boolean;
  loading?: boolean;
  optional?: boolean;
  useAquiloDesign?: boolean;
};

export const RadioWithFileReference: React.FC<RadioWithFileReferenceProps> = ({
  label,
  value,
  url,
  filename,
  onUrlChanged,
  onFileDeleted,
  onFileChanged,
  onRadioChanged,
  hideUrlInput = false,
  error = null,
  disabled = false,
  loading = false,
  optional = false,
  useAquiloDesign = false,
}) => {
  const hasFile = Boolean(filename);

  if (useAquiloDesign) {
    return (
      <>
        {!disabled && !hideUrlInput && (
          <div>
            {hasFile ? (
              <div className="flex items-center gap-[6px]">
                <div className="max-w-[30ch] truncate text-ellipsis text-blue-600 underline text-[14px] font-medium leading-[21px]">
                  {filename}
                </div>
                <div
                  className="w-[24px] h-[24px] shrink-0 flex items-center justify-center rounded-full border border-solid border-neutral-300 hover:bg-neutral-100 hover:border-neutral-400 transition-all duration-200 cursor-pointer"
                  onClick={onFileDeleted}
                >
                  <XMarkIcon className="w-[14px] h-[14px] text-neutral-500" />
                </div>
              </div>
            ) : (
              <UrlOrFileInput
                url={url}
                onUrlChanged={onUrlChanged}
                filename={filename}
                onFileChanged={onFileChanged}
                disabled={disabled}
                error={error}
                optional={optional}
                useAquiloDesign={useAquiloDesign}
              />
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {!useAquiloDesign && (
        <FormControlLabel
          value={value}
          control={<Radio />}
          onChange={onRadioChanged}
          label={label}
          disabled={disabled || loading}
          className="mr-0"
        />
      )}

      {!disabled && !hideUrlInput && (
        <div className={useAquiloDesign ? "" : "ml-xl"}>
          {hasFile ? (
            <TextField
              size="small"
              variant="outlined"
              disabled
              value={filename ?? ""}
              fullWidth
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon>
                      <Attachment />
                    </Icon>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={onFileDeleted} size="small" edge="end">
                      <Close />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          ) : (
            <UrlOrFileInput
              url={url}
              onUrlChanged={onUrlChanged}
              filename={filename}
              onFileChanged={onFileChanged}
              disabled={disabled}
              error={error}
              optional={optional}
              useAquiloDesign={useAquiloDesign}
            />
          )}

          {Boolean(error) && (
            <Alert className="mt-sm" color="error">
              {error}
            </Alert>
          )}
        </div>
      )}
    </>
  );
};
